<script setup lang="ts">
import { computed } from 'vue'

/* =========================
   Types
========================= */

type ColumnType = 'text' | 'number'

interface BadgeStyle {
  bgColor: string
  textColor: string
}

interface Column {
  key: string
  label: string
  class?: string
  type?: ColumnType
  truncate?: boolean
  subKey?: string
  width?: string
  badgeMap?: Record<string, BadgeStyle>
  linkKey?: string
}

interface Action {
  label: string | ((row: Row) => string)
  icon?: string
}

type Row = Record<string, unknown>

/* =========================
   Props
========================= */

const props = defineProps<{
  columns: Column[]
  rows: Row[]
  actions?: Action[]
}>()

/* =========================
   Emits
========================= */

const emit = defineEmits<{
  (e: 'primaryAction', row: Row): void
  (e: 'secondaryAction', row: Row): void
  (e: 'thirdAction', row: Row): void
}>()

/* =========================
   Computed
========================= */
const hasCustomWidths = computed(() => props.columns.some((col) => col.width))

const gridTemplateColumns = computed(() => {
  if (!hasCustomWidths.value) {
    return undefined
  }

  const columns = props.columns.map((col) => col.width || 'auto').join(' ')

  return props.actions?.length ? `${columns} auto` : columns
})

const actionsColSpan = computed<number>(() => {
  const totalSpan = props.columns.reduce((total, col) => {
    const match = col.class?.match(/col-span-(\d+)/)

    return total + (match ? parseInt(match[1]) : 1)
  }, 0)

  return Math.max(12 - totalSpan, 0)
})

/* =========================
   Methods
========================= */

const formatValue = (value: unknown, column: Column): string | unknown => {
  if (column.type === 'number' && value != null) {
    const num = Number(value)
    if (!isNaN(num)) {
      return num.toLocaleString()
    }
  }

  return value
}

const hasBadge = (column: Column, value: unknown): boolean =>
  !!column.badgeMap && column.badgeMap[String(value)] != null

const badgeBg = (column: Column, value: unknown): string => column.badgeMap?.[String(value)]?.bgColor ?? ''

const badgeText = (column: Column, value: unknown): string => column.badgeMap?.[String(value)]?.textColor ?? ''
</script>

<template>
  <div class="w-full">

    <!-- ================= Header ================= -->

    <div class="w-full hidden lg:grid items-center text-[#1b325a] text-[10px] font-black p-4"
      :class="!hasCustomWidths ? 'grid-cols-12' : ''" :style="hasCustomWidths ? { gridTemplateColumns } : undefined">
      <div v-for="col in props.columns" :key="col.key" :class="col.class">
        <span class="text-[#1b325a] text-[10px] font-black">
          {{ col.label }}
        </span>
      </div>

      <div v-if="props.actions?.length" :class="[
        !hasCustomWidths ? `col-span-${actionsColSpan}` : '',
        'text-center'
      ]">
        عملیات
      </div>
    </div>

    <!-- ================= Body ================= -->

    <div class="grid divide-y divide-[#eaeef5] bg-white rounded-xl px-0 py-0 pb-0 overflow-visible">
      <div v-for="(row, index) in props.rows" :key="index"
        class="w-full flex flex-col gap-1 lg:gap-0 lg:grid lg:items-center text-[#000000DE] px-4 pt-4 pb-4 transition-colors hover:bg-[#f5f7fa]"
        :class="!hasCustomWidths ? 'grid-cols-12' : ''" :style="hasCustomWidths ? { gridTemplateColumns } : undefined">

        <!-- Columns -->

        <div v-for="col in props.columns" :key="col.key" :class="col.class">
          <div class="flex flex-col gap-0.5" :class="col.truncate ? 'relative group min-w-0' : ''">

            <span class="block lg:hidden text-[13px] font-bold">
              {{ col.label }}:
            </span>

            <!-- Main Value -->

            <template v-if="hasBadge(col, row[col.key])">
              <Badge size="sm" :label="String(formatValue(row[col.key], col))" :bg-color="badgeBg(col, row[col.key])"
                :text-color="badgeText(col, row[col.key])" />
            </template>

            <a v-else-if="col.linkKey && row[col.linkKey] != null" :href="String(row[col.linkKey])" target="_blank"
              rel="noopener noreferrer" class="text-[#2d5396] hover:underline">
              {{ formatValue(row[col.key], col) }}
            </a>

            <span v-else :class="col.truncate ? 'truncate block max-w-[180px]' : ''">
              {{ formatValue(row[col.key], col) }}
            </span>

            <!-- Sub Value -->

            <span v-if="
              col.subKey &&
              row[col.subKey] != null &&
              row[col.subKey] !== ''
            " class="text-[11px] text-[#8996ac] truncate max-w-[180px]">
              {{ row[col.subKey] }}
            </span>

            <!-- Tooltip -->

            <div v-if="col.truncate"
              class="pointer-events-none absolute bottom-full right-0 z-20 mb-1 hidden w-fit max-w-[260px] whitespace-normal rounded-md bg-[#1b325a] px-2.5 py-1.5 text-[11px] leading-5 text-white shadow-lg group-hover:block">
              {{ formatValue(row[col.key], col) }}

              <template v-if="col.subKey && row[col.subKey]">
                <br />

                <span class="text-[#c7d0e0]">
                  {{ row[col.subKey] }}
                </span>
              </template>
            </div>

          </div>
        </div>

        <!-- Actions -->

        <div v-if="props.actions?.length" class="flex items-center justify-end"
          :class="!hasCustomWidths ? `col-span-${actionsColSpan}` : ''">
          <template v-for="(action, i) in props.actions" :key="i">

            <!-- Icon Action -->

            <Button v-if="action.icon" type="button" :isAdmin="true" variant="outline"
              class="flex items-center justify-center px-3.5! h-8! me-2 cursor-pointer"
              @click="emit('secondaryAction', row)">
              <img :src="action.icon" width="22" height="22" />
            </Button>

            <!-- Primary Action -->

            <Button v-else-if="i === 0" :isAdmin="true"
              class="w-fit text-nowrap rounded-lg! px-3.5! h-8! py-0 text-[13px] font-bold! me-2"
              @click="emit('primaryAction', row)">
              {{
                typeof action.label === 'function'
                  ? action.label(row)
                  : action.label
              }}
            </Button>

            <!-- Secondary Action -->

            <Button v-else-if="i === 1" :isAdmin="true" variant="outline"
              class="w-fit text-nowrap rounded-lg! px-3.5! h-8! py-0 text-[13px] font-bold! me-2"
              @click="emit('secondaryAction', row)">
              {{
                typeof action.label === 'function'
                  ? action.label(row)
                  : action.label
              }}
            </Button>


            <!-- Third Action -->

            <Button v-else :isAdmin="true" variant="outline"
              class="w-fit text-nowrap rounded-lg! px-3.5! h-8! py-0 text-[13px] font-bold! me-2"
              @click="emit('thirdAction', row)">
              {{
                typeof action.label === 'function'
                  ? action.label(row)
                  : action.label
              }}
            </Button>

          </template>
        </div>
      </div>
    </div>
  </div>
</template>
